import { isJsonbRepresentableString } from "../../contracts/src/schemas/shared/jsonb-string.ts";
import { isRecord } from "./predicates.ts";
import type {
  ContentBlockNode,
  ContentDocument,
  ContentHeadingNode,
  ContentInlineNode,
  ContentListItemNode,
  ContentMark,
  ContentOrderedListNode,
  ContentParagraphNode,
} from "./types.ts";

/** Structural and safety limits enforced on a content document. */
export const CONTENT_LIMITS = {
  maxJsonBytes: 65_536,
  maxLinkHrefLength: 2048,
  maxNodes: 1024,
  maxOrderedListStart: 1_000_000,
  maxTextLength: 10_000,
} as const;

const HEADING_LEVELS = new Set([2, 3]);

// Rejects C0 control characters and DEL inside link hrefs. This mirrors the
// contract's href safety check so meaningful, injection-free links pass alike.
// biome-ignore lint/suspicious/noControlCharactersInRegex: intentional href guard
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;

type ContentValidationState = {
  nodeCount: number;
  textLength: number;
};

/**
 * Validates an unknown value as a safe {@link ContentDocument}.
 *
 * Enforces the exact structural shape (permitted block, inline, and mark
 * nodes), the aggregate limits in {@link CONTENT_LIMITS}, and link safety.
 * Returns `true` only when the value can be rendered without any raw HTML.
 */
export const isContentDocument = (value: unknown): value is ContentDocument => {
  if (!isRecord(value) || value.type !== "doc" || !Array.isArray(value.content)) {
    return false;
  }

  if (!hasOnlyKeys(value, ["content", "type"])) {
    return false;
  }

  if (!isWithinJsonByteLimit(value)) {
    return false;
  }

  const state: ContentValidationState = { nodeCount: 0, textLength: 0 };

  if (!countNode(state)) {
    return false;
  }

  if (!value.content.every((block) => isBlockNode(block, state))) {
    return false;
  }

  return state.textLength <= CONTENT_LIMITS.maxTextLength;
};

/**
 * Returns whether a link href is a safe, absolute HTTP(S) URL or a meaningful
 * `mailto:` address. Relative URLs, other schemes, and control characters are
 * rejected so links never become an injection vector.
 */
export const isSafeContentHref = (href: unknown): href is string => {
  if (typeof href !== "string" || href.length === 0) {
    return false;
  }

  if (href.length > CONTENT_LIMITS.maxLinkHrefLength) {
    return false;
  }

  if (CONTROL_CHARACTERS.test(href) || /\s/.test(href)) {
    return false;
  }

  // Lone surrogates are not storable in JSONB and never appear in a valid URL.
  if (!isJsonbRepresentableString(href)) {
    return false;
  }

  const mailtoMatch = /^mailto:(.+)$/i.exec(href);
  if (mailtoMatch) {
    const address = mailtoMatch[1] ?? "";

    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address);
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }

  const protocol = url.protocol.toLowerCase();
  if (protocol !== "http:" && protocol !== "https:") {
    return false;
  }

  return url.hostname.length > 0;
};

const isBlockNode = (value: unknown, state: ContentValidationState): boolean => {
  if (!isRecord(value)) {
    return false;
  }

  switch (value.type) {
    case "paragraph":
      return isParagraphNode(value, state);
    case "heading":
      return isHeadingNode(value, state);
    case "bulletList":
      return isListNode(value, state, false);
    case "orderedList":
      return isListNode(value, state, true);
    default:
      return false;
  }
};

const isParagraphNode = (
  value: Record<string, unknown>,
  state: ContentValidationState,
): value is ContentParagraphNode => {
  if (!countNode(state) || !hasOnlyKeys(value, ["content", "type"])) {
    return false;
  }

  return isOptionalInlineContent(value.content, state);
};

const isHeadingNode = (
  value: Record<string, unknown>,
  state: ContentValidationState,
): value is ContentHeadingNode => {
  if (!countNode(state) || !hasOnlyKeys(value, ["attrs", "content", "type"])) {
    return false;
  }

  if (
    !isRecord(value.attrs) ||
    !hasOnlyKeys(value.attrs, ["level"]) ||
    !HEADING_LEVELS.has(value.attrs.level as number)
  ) {
    return false;
  }

  return isOptionalInlineContent(value.content, state);
};

const isListNode = (
  value: Record<string, unknown>,
  state: ContentValidationState,
  ordered: boolean,
): value is ContentOrderedListNode => {
  const allowedKeys = ordered ? ["attrs", "content", "type"] : ["content", "type"];
  if (!countNode(state) || !hasOnlyKeys(value, allowedKeys)) {
    return false;
  }

  if (ordered && value.attrs !== undefined && !isOrderedListAttrs(value.attrs)) {
    return false;
  }

  if (!Array.isArray(value.content) || value.content.length === 0) {
    return false;
  }

  return value.content.every((item) => isListItemNode(item, state));
};

const isOrderedListAttrs = (value: unknown): boolean => {
  if (!isRecord(value) || !hasOnlyKeys(value, ["start"])) {
    return false;
  }

  const start = value.start;

  return (
    typeof start === "number" &&
    Number.isInteger(start) &&
    start >= 1 &&
    start <= CONTENT_LIMITS.maxOrderedListStart
  );
};

const isListItemNode = (
  value: unknown,
  state: ContentValidationState,
): value is ContentListItemNode => {
  if (!isRecord(value) || value.type !== "listItem" || !hasOnlyKeys(value, ["content", "type"])) {
    return false;
  }

  if (!countNode(state) || !Array.isArray(value.content) || value.content.length === 0) {
    return false;
  }

  // List items may only contain paragraphs; nested lists are disallowed.
  return value.content.every((child) => {
    return isRecord(child) && child.type === "paragraph" && isParagraphNode(child, state);
  });
};

const isOptionalInlineContent = (value: unknown, state: ContentValidationState): boolean => {
  if (value === undefined) {
    return true;
  }

  if (!Array.isArray(value)) {
    return false;
  }

  return value.every((node) => isInlineNode(node, state));
};

const isInlineNode = (
  value: unknown,
  state: ContentValidationState,
): value is ContentInlineNode => {
  if (!isRecord(value)) {
    return false;
  }

  if (value.type === "hardBreak") {
    return countNode(state) && hasOnlyKeys(value, ["type"]);
  }

  if (value.type !== "text") {
    return false;
  }

  if (!countNode(state) || !hasOnlyKeys(value, ["marks", "text", "type"])) {
    return false;
  }

  if (typeof value.text !== "string" || value.text.length === 0) {
    return false;
  }

  // Reject NUL and lone surrogates: JSONB cannot store them, so the contract
  // rejects them too. Every other string is preserved verbatim.
  if (!isJsonbRepresentableString(value.text)) {
    return false;
  }

  state.textLength += value.text.length;

  return areMarksValid(value.marks);
};

const areMarksValid = (value: unknown): boolean => {
  if (value === undefined) {
    return true;
  }

  if (!Array.isArray(value) || value.length > 3) {
    return false;
  }

  if (!value.every(isMark)) {
    return false;
  }

  return new Set(value.map((mark) => mark.type)).size === value.length;
};

const isMark = (value: unknown): value is ContentMark => {
  if (!isRecord(value)) {
    return false;
  }

  if (value.type === "bold" || value.type === "italic") {
    return hasOnlyKeys(value, ["type"]);
  }

  if (value.type !== "link" || !hasOnlyKeys(value, ["attrs", "type"])) {
    return false;
  }

  if (!isRecord(value.attrs) || !hasOnlyKeys(value.attrs, ["href"])) {
    return false;
  }

  return isSafeContentHref(value.attrs.href);
};

const countNode = (state: ContentValidationState): boolean => {
  state.nodeCount += 1;

  return state.nodeCount <= CONTENT_LIMITS.maxNodes;
};

const isWithinJsonByteLimit = (value: unknown): boolean => {
  const encoder = new TextEncoder();

  try {
    return encoder.encode(JSON.stringify(value)).byteLength <= CONTENT_LIMITS.maxJsonBytes;
  } catch {
    return false;
  }
};

const hasOnlyKeys = (value: Record<string, unknown>, keys: string[]): boolean => {
  return Object.keys(value).every((key) => keys.includes(key));
};

export type { ContentBlockNode };
